/**
 * HmListShell — wspólny szkielet kartotek modułu Handel i Magazyn.
 *
 * Pięć ekranów (sprzedaż, magazyn, zamówienia, kasa i bank, towary) różni się kolumnami i
 * zasobem, a resztę mają identyczną: belkę akcji, lewy panel z gotowymi zakresami dat i
 * licznikami rodzajów, stronicowanie po naszej stronie oraz zestaw stanów „nie mam skąd czytać".
 * Trzymanie tego w jednym miejscu jest tu ważniejsze niż zwykle, bo te stany to połowa wartości
 * ekranu — rozjazd między kartotekami oznaczałby, że w jednej użytkownik dostaje radę, a w innej
 * pustą tabelę.
 *
 * Kartoteka towarów nie jest datowana — dlatego panel dat jest opcjonalny (`showDateFilter`).
 */

import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FK_MIN_DATE_YEAR, type HmListResponse } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  BarChart3,
  Download,
  FileSpreadsheet,
  FileText,
  type LucideIcon,
  Plug,
  Plus,
  Printer,
  RefreshCw,
  Settings2,
} from 'lucide-react';

import { Button } from '@/components/Button';
import type { ColumnDefinition } from '@/components/configurable-table';
import { ConfigurableTable } from '@/components/configurable-table';
import { DatePicker } from '@/components/DatePicker';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const inputCls =
  'rounded-md border border-border-primary bg-bg-primary px-2.5 py-1.5 text-sm text-text-primary';

export type RangePreset =
  | 'today'
  | 'yesterday'
  | 'last30'
  | 'last60'
  | 'last90'
  | 'thisMonth'
  | 'thisQuarter'
  | 'thisYear'
  | 'prevMonth'
  | 'prevQuarter'
  | 'prevYear'
  | 'all'
  | 'custom';

const PRESET_ORDER: RangePreset[] = [
  'today',
  'last30',
  'thisMonth',
  'thisQuarter',
  'thisYear',
  'prevMonth',
  'prevQuarter',
  'prevYear',
];

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Zakres dla gotowego wyboru, liczony na datach LOKALNYCH — `toISOString()` przesuwa o strefę
 * i potrafi cofnąć „pierwszy dzień miesiąca" o dobę, ucinając dokumenty z pierwszego.
 */
export function rangeFor(preset: RangePreset): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3);
  switch (preset) {
    case 'today':
      return { from: iso(now), to: iso(now) };
    case 'yesterday': {
      const d = new Date(now);
      d.setDate(d.getDate() - 1);
      return { from: iso(d), to: iso(d) };
    }
    case 'last30':
    case 'last60':
    case 'last90': {
      const days = preset === 'last30' ? 30 : preset === 'last60' ? 60 : 90;
      const from = new Date(now);
      from.setDate(from.getDate() - (days - 1));
      return { from: iso(from), to: iso(now) };
    }
    case 'thisMonth':
      return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
    case 'prevMonth':
      return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case 'thisQuarter':
      return { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) };
    case 'prevQuarter':
      return { from: iso(new Date(y, q * 3 - 3, 1)), to: iso(new Date(y, q * 3, 0)) };
    case 'thisYear':
      return { from: iso(new Date(y, 0, 1)), to: iso(new Date(y, 11, 31)) };
    case 'prevYear':
      return { from: iso(new Date(y - 1, 0, 1)), to: iso(new Date(y - 1, 11, 31)) };
    // „Wszystkie daty" to nie brak zakresu — zakres MUSI polecieć do Symfonii, bo bez niego
    // dostalibyśmy bieżący miesiąc. Dolna granica to najwcześniejszy rocznik, który silnik
    // Symfonii w ogóle przyjmuje (niżej wywala się przepełnieniem).
    case 'all':
      return { from: `${FK_MIN_DATE_YEAR}-01-01`, to: iso(now) };
    case 'custom':
      return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  }
}

/** Data z Symfonii bywa z częścią czasową — na listach pokazujemy sam dzień. */
export function day(value: string | null): string {
  return value ? value.slice(0, 10) : '—';
}

export interface HmListShellProps<T> {
  title: string;
  /** Ścieżka zasobu API bez parametrów, np. `warehouse-documents`. */
  resource: string;
  /** Klucz pamięci podręcznej — musi być różny dla każdej kartoteki. */
  queryKey: string;
  tableId: string;
  testIdPrefix: string;
  columns: ColumnDefinition<T>[];
  getRowId: (row: T) => string;
  emptyIcon: LucideIcon;
  /** Kartoteki nie-datowane (towary) chowają panel dat i nie wysyłają zakresu. */
  showDateFilter?: boolean;
  /** Grupowanie w lewym panelu — np. rodzaj dokumentu; `null` = brak sekcji. */
  groupBy?: { label: string; of: (row: T) => string } | null;
  onRowClick?: (row: T) => void;
  /**
   * Akcja „Nowy". Podana = przycisk działa; pominięta = pozostaje wyłączony, bo dana kartoteka
   * nie ma jeszcze wystawiania. Wyłączony przycisk mówi „to tu będzie", ukryty kazałby zgadywać.
   */
  onNew?: () => void;
  /**
   * Akcja „Zestawienia" — kartoteka podaje własny element (menu raportów), bo listy różnią się
   * tym, jakie raporty mają sens. Pominięta = przycisk zostaje wyłączony, jak dotąd.
   *
   * Dostaje AKTUALNY stan listy: zakres dat i zaznaczone wiersze. Raport „dla zaznaczonych" musi
   * wiedzieć jedno i drugie — zakres, bo z niego pobiera dokumenty, i zaznaczenie, bo do niego
   * zawęża wynik.
   */
  renderReports?: (ctx: { dateFrom: string; dateTo: string; selected: T[] }) => ReactNode;
  /**
   * Akcja belki działająca na ZAZNACZONYCH wierszach (np. skierowanie do akceptacji).
   * Renderowana za „Odśwież", bo dotyczy wyboru użytkownika, nie samej kartoteki.
   */
  renderSelectionAction?: (ctx: { selected: T[] }) => ReactNode;
  /** Zaznaczanie wierszy — włączane przez kartotekę, która ma co z zaznaczeniem zrobić. */
  selectable?: boolean;
  /**
   * Dodatkowe parametry zapytania listy (np. `withKsef`). Wchodzą do klucza pamięci podręcznej,
   * więc ich zmiana odświeża listę — inaczej przełącznik nie miałby skutku.
   */
  extraParams?: Record<string, string>;
  /**
   * Komunikat nad tabelą wyliczany z odpowiedzi — do rzeczy specyficznych dla jednej kartoteki
   * (np. „zakres za szeroki, żeby dociągnąć statusy KSeF"). Szkielet nie zna tych reguł.
   */
  renderNotice?: (data: HmListResponse<T>) => ReactNode;
}

export function HmListShell<T>({
  title,
  resource,
  queryKey,
  tableId,
  testIdPrefix,
  columns,
  getRowId,
  emptyIcon,
  showDateFilter = true,
  groupBy = null,
  onRowClick,
  onNew,
  renderReports,
  renderSelectionAction,
  selectable = false,
  extraParams,
  renderNotice,
}: HmListShellProps<T>) {
  const { t } = useTranslation();
  const scope = useActiveCompanyScope();

  const [preset, setPreset] = useState<RangePreset>('thisMonth');
  const initial = useMemo(() => rangeFor('thisMonth'), []);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [group, setGroup] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const companyId = scope.companyId;

  const query = useQuery({
    queryKey: [
      'handel-magazyn',
      queryKey,
      companyId,
      showDateFilter ? dateFrom : '',
      showDateFilter ? dateTo : '',
    ],
    enabled: !!companyId,
    // Symfonia nie odpowiada? Ponawianie tylko wydłuża czekanie — pokazujemy błąd od razu.
    retry: false,
    queryFn: async (): Promise<HmListResponse<T>> => {
      const params = new URLSearchParams({ companyId: companyId! });
      if (showDateFilter) {
        params.set('dateFrom', dateFrom);
        params.set('dateTo', dateTo);
      }
      for (const [k, v] of Object.entries(extraParams ?? {})) params.set(k, v);
      const res = await api.get<HmListResponse<T>>(`/handel-magazyn/${resource}?${params}`);
      return res.data;
    },
  });

  const allItems = useMemo(() => query.data?.items ?? [], [query.data]);

  /**
   * Zaznaczone wiersze liczymy z PEŁNEJ listy, nie z bieżącej strony — zaznaczenie ma przeżyć
   * przewracanie stron, inaczej „wykonaj dla zaznaczonych" milczkiem gubiłoby część dokumentów.
   */
  const selected = useMemo(() => {
    if (!selectable || selectedIds.length === 0) return [];
    const chosen = new Set(selectedIds);
    return allItems.filter((item) => chosen.has(getRowId(item)));
  }, [selectable, selectedIds, allItems, getRowId]);

  // Nowy zakres dat = inne dokumenty. Zostawione zaznaczenie wskazywałoby wiersze, których na
  // liście już nie ma.
  useEffect(() => {
    setSelectedIds([]);
  }, [dateFrom, dateTo, companyId]);

  const groupCounts = useMemo(() => {
    if (!groupBy) return [];
    const counts = new Map<string, number>();
    for (const it of allItems) {
      const key = groupBy.of(it);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [allItems, groupBy]);

  const filtered = useMemo(
    () => (groupBy && group ? allItems.filter((i) => groupBy.of(i) === group) : allItems),
    [allItems, group, groupBy],
  );

  // Zmiana zakresu/grupy wraca na pierwszą stronę — inaczej user ląduje na pustej stronie 7.
  useEffect(() => {
    setPage(0);
  }, [dateFrom, dateTo, group]);

  const pageItems = useMemo(
    () => filtered.slice(page * pageSize, page * pageSize + pageSize),
    [filtered, page, pageSize],
  );

  function applyPreset(next: RangePreset) {
    setPreset(next);
    const r = rangeFor(next);
    setDateFrom(r.from);
    setDateTo(r.to);
  }

  const problem = useMemo(() => {
    const err = query.error;
    if (!err || !isAxiosError(err)) return null;
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    return data?.error ? { code: data.error, message: data.message ?? null } : null;
  }, [query.error]);

  return (
    <div className="p-4">
      <h1 className="mb-3 text-xl font-semibold text-text-primary">{title}</h1>

      {/* Belka akcji. Wyłączone przyciski to INFORMACJA o zakresie, nie niedoróbka: odpowiadają
          funkcjom, których Fala 1 nie dowozi (wystawianie, zestawienia, eksport, wydruki zbiorcze).
          Ukrycie ich sprawiłoby, że ekran „zmienia się" po dowiezieniu. */}
      <div className="mb-3 flex items-center gap-1 rounded-md border border-border-primary bg-bg-secondary px-2 py-1.5">
        <ToolbarButton
          icon={<Plus className="h-4 w-4" />}
          label={t('tenant.handelMagazyn.toolbar.new')}
          enabled={!!onNew}
          onClick={onNew}
        />
        <ToolbarButton
          icon={<Settings2 className="h-4 w-4" />}
          label={t('tenant.handelMagazyn.toolbar.operations')}
        />
        {renderReports ? (
          renderReports({ dateFrom, dateTo, selected })
        ) : (
          <ToolbarButton
            icon={<FileSpreadsheet className="h-4 w-4" />}
            label={t('tenant.handelMagazyn.toolbar.reports')}
          />
        )}
        <ToolbarButton
          icon={<BarChart3 className="h-4 w-4" />}
          label={t('tenant.handelMagazyn.toolbar.charts')}
        />
        <ToolbarButton
          icon={<Download className="h-4 w-4" />}
          label={t('tenant.handelMagazyn.toolbar.export')}
        />
        <ToolbarButton
          icon={<Printer className="h-4 w-4" />}
          label={t('tenant.handelMagazyn.toolbar.print')}
        />
        <ToolbarButton
          icon={<FileText className="h-4 w-4" />}
          label={t('tenant.handelMagazyn.toolbar.positions')}
        />
        <div className="mx-1 h-6 w-px bg-border-primary" />
        <ToolbarButton
          icon={<RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />}
          label={t('tenant.handelMagazyn.refresh')}
          enabled
          onClick={() => void query.refetch()}
        />
        {/* Akcja na ZAZNACZONYCH wierszach — za odświeżeniem, bo dotyczy wyboru użytkownika,
            a nie samej kartoteki. Kartoteki bez obiegów po prostu jej nie podają. */}
        {renderSelectionAction ? renderSelectionAction({ selected }) : null}
      </div>

      <div className="flex gap-4">
        {(showDateFilter || groupBy) && (
          <aside className="w-56 shrink-0 space-y-4">
            {showDateFilter && (
              <section className="rounded-md border border-border-primary bg-bg-secondary p-2">
                <h2 className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
                  {t('tenant.handelMagazyn.filters.title')}
                </h2>
                <ul>
                  {PRESET_ORDER.map((p) => (
                    <li key={p}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => applyPreset(p)}
                        className={`w-full justify-start ${
                          preset === p ? 'bg-bg-tertiary font-medium text-text-primary' : ''
                        }`}
                      >
                        {t(`tenant.handelMagazyn.filters.preset.${p}`)}
                      </Button>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 space-y-1 border-t border-border-primary px-1 pt-2">
                  <label className="flex flex-col text-[11px] text-text-secondary">
                    {t('tenant.handelMagazyn.filters.dateFrom')}
                    <DatePicker
                      value={dateFrom}
                      onChange={(v) => {
                        setDateFrom(v || '');
                        setPreset('custom');
                      }}
                      className={inputCls}
                    />
                  </label>
                  <label className="flex flex-col text-[11px] text-text-secondary">
                    {t('tenant.handelMagazyn.filters.dateTo')}
                    <DatePicker
                      value={dateTo}
                      onChange={(v) => {
                        setDateTo(v || '');
                        setPreset('custom');
                      }}
                      className={inputCls}
                    />
                  </label>
                </div>
              </section>
            )}

            {groupBy && (
              <section className="rounded-md border border-border-primary bg-bg-secondary p-2">
                <h2 className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
                  {groupBy.label}
                </h2>
                <ul>
                  <li>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setGroup(null)}
                      className={`w-full justify-between ${
                        group === null ? 'bg-bg-tertiary font-medium text-text-primary' : ''
                      }`}
                    >
                      <span>{t('tenant.handelMagazyn.filters.all')}</span>
                      <span className="text-xs text-text-tertiary">{allItems.length}</span>
                    </Button>
                  </li>
                  {groupCounts.map(([key, count]) => (
                    <li key={key}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setGroup(key)}
                        className={`w-full justify-between ${
                          group === key ? 'bg-bg-tertiary font-medium text-text-primary' : ''
                        }`}
                      >
                        <span className="truncate font-mono text-xs">{key}</span>
                        <span className="text-xs text-text-tertiary">{count}</span>
                      </Button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </aside>
        )}

        <div className="min-w-0 flex-1">
          {!companyId && (
            <div className="rounded-md border border-border-primary bg-bg-secondary p-6 text-center text-sm text-text-secondary">
              {t('tenant.handelMagazyn.pickCompany')}
            </div>
          )}

          {companyId && problem && (
            <div className="rounded-md border border-border-primary bg-bg-secondary p-6 text-center">
              <Plug className="mx-auto mb-2 h-6 w-6 text-text-tertiary" />
              <p className="text-sm font-medium text-text-primary">
                {t(`tenant.handelMagazyn.problem.${problem.code}`, {
                  defaultValue: t('tenant.handelMagazyn.problem.UNKNOWN'),
                })}
              </p>
              {problem.message && (
                <p className="mt-1 text-xs text-text-secondary">{problem.message}</p>
              )}
            </div>
          )}

          {companyId && !problem && (
            <>
              {query.data && renderNotice?.(query.data)}
              {query.data?.truncated && (
                <div className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  {t('tenant.handelMagazyn.truncated')}
                </div>
              )}
              <ConfigurableTable<T>
                tableId={tableId}
                testIdPrefix={testIdPrefix}
                columns={columns}
                data={pageItems}
                isLoading={query.isLoading}
                getRowId={getRowId}
                totalItems={filtered.length}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(0);
                }}
                pageSizeOptions={[25, 50, 100, 200]}
                onRowClick={onRowClick}
                selectable={selectable}
                selectedIds={selectable ? selectedIds : undefined}
                onSelectionChange={selectable ? setSelectedIds : undefined}
                // Dane spoza naszej bazy — nie zapisujemy układu kolumn (mirror KsefInvoices).
                persistConfig={false}
                showViewModeToggle={false}
                emptyIcon={emptyIcon}
                emptyTitle={t('tenant.handelMagazyn.empty.title')}
                emptyDescription={t('tenant.handelMagazyn.empty.description')}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Przycisk belki — ikona nad podpisem, jak w kartotece desktopowego Handlu. */
export function ToolbarButton({
  icon,
  label,
  enabled,
  onClick,
  testId,
  ariaExpanded,
}: {
  icon: ReactNode;
  label: string;
  enabled?: boolean;
  onClick?: () => void;
  testId?: string;
  /** Ustawiane, gdy przycisk rozwija menu — czytnik ekranu ma wiedzieć, że coś się otworzyło. */
  ariaExpanded?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={!enabled}
      onClick={onClick}
      title={enabled ? label : `${label} — ${t('tenant.handelMagazyn.toolbar.soon')}`}
      className="min-w-[64px] flex-col gap-0.5 px-2 py-1 text-[11px]"
      data-testid={testId}
      {...(ariaExpanded === undefined
        ? {}
        : { 'aria-haspopup': 'menu', 'aria-expanded': ariaExpanded })}
    >
      {icon}
      <span>{label}</span>
    </Button>
  );
}
